import { Component, computed, Inject, isSignal, signal, Signal, TemplateRef } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { BehaviorSubject, isObservable, Observable, of } from 'rxjs';
import { combineLogicalOr, isNullOrUndefined } from "../../utils";
import { toSignal } from '@angular/core/rxjs-interop';

export interface GenericAlertData {
  title?: string;
  titleIcon?: string;
  content?: string | TemplateRef<any>;
  message?: string;
  buttons?: GenericAlertButton[];
  showClose?: boolean;
  busy?: Observable<boolean> | Signal<boolean>;
}

export class GenericAlertButton {
  constructor(
    public readonly title: string,
    public readonly color: string,
    public readonly value: any,
    public readonly close = true,
    public readonly click: (dialogRef: MatDialogRef<GenericAlertDialog>) => (void | Promise<void>) = null) { }

  withValue(value: any) {
    return new GenericAlertButton(this.title, this.color, value, this.close, this.click);
  }

  dontClose() {
    return new GenericAlertButton(this.title, this.color, this.value, false, this.click);
  }

  onClick(click: (dialogRef: MatDialogRef<GenericAlertDialog>) => (void | Promise<void>)) {
    return new GenericAlertButton(this.title, this.color, this.value, this.close, click);
  }

  public static readonly cancel = new GenericAlertButton('Cancel', 'outline', null);
  public static readonly close = new GenericAlertButton('Close', 'outline', null);
  public static readonly abort = new GenericAlertButton('Abort', 'outline', null);
  public static readonly no = new GenericAlertButton('No', 'outline', false);

  public static readonly continue = new GenericAlertButton('Continue', 'primary', true);
  public static readonly save = new GenericAlertButton('Save', 'primary', true);
  public static readonly ok = new GenericAlertButton('OK', 'primary', true);
  public static readonly confirm = new GenericAlertButton('Confirm', 'primary', true);
  public static readonly yes = new GenericAlertButton('Yes', 'primary', true);
  public static readonly delete = new GenericAlertButton('Delete', 'red-2', true);
  public static readonly remove = new GenericAlertButton('Remove', 'red-2', true);
  public static readonly discard = new GenericAlertButton('Discard', 'red-2', true);
  public static readonly refresh = new GenericAlertButton('Refresh', 'primary', true);
}

@Component({
  templateUrl: 'generic-alert.dialog.html',
  styleUrls: ['generic-alert.dialog.scss'],
  standalone: false
})
export class GenericAlertDialog {
  title: string;
  titleIcon: string;
  contentString: string;
  contentTemplate: TemplateRef<any>;
  message: string;
  buttons: GenericAlertButton[] = [GenericAlertButton.close];
  showClose = false;

  noContent = false;
  contentIsTemplateRef = false;

  constructor(
    public dialogRef: MatDialogRef<GenericAlertDialog>,
    @Inject(MAT_DIALOG_DATA) public data: GenericAlertData) {
    this.title = this.data.title ?? 'Alert';
    this.titleIcon = this.data.titleIcon;
    this.showClose = this.data.showClose ?? false;
    
    this.contentTemplate = null;
    this.contentString = null;

    if (!isNullOrUndefined(data.content)) {
      this.noContent = false;
      
      if (data.content instanceof TemplateRef) {
        this.contentTemplate = data.content;
      }
      else {
        this.contentString = data.content;
      }
    }
    else {
      this.noContent = true;

      this.message = this.data.message ?? 'An error occurred';
    }

    if (!isNullOrUndefined(data.buttons)) {
      this.buttons = data.buttons;
    }
  }

  readonly _dataBusy = 
    isObservable(this.data.busy) 
      ? toSignal(this.data.busy) 
      : isSignal(this.data.busy) ? this.data.busy : (() => false);

  readonly _busy = signal(false);
  readonly busy = computed(() =>  this._dataBusy() || this._busy());

  async onButtonClick(button?: GenericAlertButton) {
    if (button?.click) {
      this._busy.set(true);

      try {
        await button.click(this.dialogRef);
      }
      finally {
        this._busy.set(false);
      }
    }

    if (!button || !!button?.close) {
    this.dialogRef.close(button?.value);
    }
  }

  static warn(dialog: MatDialog, message: any, data: GenericAlertData = {}) {
    return dialog.open(GenericAlertDialog, {
      width: '450px',
      data: <GenericAlertData>{
        title: 'Warning',
        titleIcon: 'fas fa-exclamation-triangle',
        content: message,
        buttons: [GenericAlertButton.ok],
        ...data
      }
    }).afterClosed();
  }
}
